/**
 * Renderer canvas per la heatmap MIT — produce un PNG condivisibile.
 *
 * Disegna direttamente su un canvas 2D (no librerie esterne):
 *  - header con titolo "Quantum Planner · Heatmap {anno}"
 *  - 4 KPI stat (giorni con MIT, tasso %, streak attuale, streak record)
 *  - griglia 53×7 con livelli 0..4 (palette verde)
 *  - labels mese in alto, labels giorni a sx
 *  - footer con dominio app
 *
 * Se `compare` è passato, disegna due blocchi affiancati (sx: compare, dx: principale)
 * con un'intestazione "Confronto anni".
 *
 * Ritorna un dataURL "image/png".
 */

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const DAY_LABELS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

const LEVELS_BG = [
  '#F0F0EE',          // 0 — empty (canvas-ish)
  'rgba(34,197,94,0.22)',
  'rgba(34,197,94,0.45)',
  'rgba(34,197,94,0.70)',
  'rgba(21,128,61,0.95)',
];

// Dimensioni
const CELL = 14;
const GAP = 3;
const ROWS = 7;
const PAD = 32;
const HEADER_H = 110;
const STATS_H = 70;
const MONTH_LABEL_H = 18;
const DAY_LABEL_W = 22;
const LEGEND_H = 28;
const FOOTER_H = 36;

function blockWidth(weeksCount) {
  return DAY_LABEL_W + weeksCount * (CELL + GAP);
}
function blockGridHeight() {
  return ROWS * (CELL + GAP);
}

function drawBlock(ctx, x0, y0, hm, opts) {
  const { dark } = opts;
  const subColor = dark ? '#A3A3A3' : '#525252';
  const labelColor = dark ? '#525252' : '#A3A3A3';
  const inkColor = dark ? '#FAFAFA' : '#171717';
  const borderEmpty = dark ? '#262626' : '#E5E5E5';

  // Block title (year)
  ctx.fillStyle = inkColor;
  ctx.font = '600 18px "Inter", "Helvetica Neue", sans-serif';
  ctx.fillText(`${hm.year}`, x0, y0);

  // Stats row
  const totals = hm.totals || {};
  const stats = [
    { label: 'Giorni con MIT', value: `${totals.days_with_mit ?? 0} / ${totals.days_tracked ?? 0}` },
    { label: 'Tasso', value: `${Math.round((totals.ratio || 0) * 100)}%` },
    { label: 'Streak attuale', value: `${totals.streak_current ?? 0} gg` },
    { label: 'Streak record', value: `${totals.streak_best ?? 0} gg` },
  ];
  const statsY = y0 + 28;
  const statW = Math.floor((hm.weeks.length * (CELL + GAP) + DAY_LABEL_W) / 4);
  stats.forEach((s, i) => {
    const sx = x0 + i * statW;
    ctx.fillStyle = labelColor;
    ctx.font = '600 9px "JetBrains Mono", "Menlo", monospace';
    ctx.fillText(s.label.toUpperCase(), sx, statsY);
    ctx.fillStyle = inkColor;
    ctx.font = '600 20px "Inter", sans-serif';
    ctx.fillText(s.value, sx, statsY + 24);
  });

  // Month labels
  const gridX0 = x0 + DAY_LABEL_W;
  const gridY0 = statsY + STATS_H;
  ctx.fillStyle = labelColor;
  ctx.font = '600 9px "JetBrains Mono", monospace';
  let lastMonth = -1;
  hm.weeks.forEach((w, wi) => {
    const ref = w.find((d) => d.in_year) || w[0];
    if (!ref) return;
    const m = new Date(ref.date).getMonth();
    if (m !== lastMonth) {
      lastMonth = m;
      ctx.fillText(MONTH_LABELS[m].toUpperCase(), gridX0 + wi * (CELL + GAP), gridY0 + 10);
    }
  });

  // Day labels (only L, M, V → indices 0, 2, 4)
  const gridY1 = gridY0 + MONTH_LABEL_H;
  ctx.fillStyle = labelColor;
  ctx.font = '600 9px "JetBrains Mono", monospace';
  DAY_LABELS.forEach((g, i) => {
    if (i % 2 === 0) {
      ctx.fillText(g, x0, gridY1 + i * (CELL + GAP) + CELL - 3);
    }
  });

  // Grid cells
  hm.weeks.forEach((week, wi) => {
    week.forEach((day, di) => {
      const cx = gridX0 + wi * (CELL + GAP);
      const cy = gridY1 + di * (CELL + GAP);
      const muted = !day.in_year || day.is_future;
      const lvl = LEVELS_BG[day.level] || LEVELS_BG[0];
      ctx.globalAlpha = muted ? 0.35 : 1;
      ctx.fillStyle = lvl;
      ctx.fillRect(cx, cy, CELL, CELL);
      if (day.level === 0) {
        ctx.strokeStyle = borderEmpty;
        ctx.lineWidth = 1;
        ctx.strokeRect(cx + 0.5, cy + 0.5, CELL - 1, CELL - 1);
      }
      if (day.is_today) {
        ctx.strokeStyle = '#DC2626';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(cx - 0.5, cy - 0.5, CELL + 1, CELL + 1);
      }
    });
  });
  ctx.globalAlpha = 1;

  // Legend
  const legendY = gridY1 + blockGridHeight() + 12;
  ctx.fillStyle = subColor;
  ctx.font = '600 9px "JetBrains Mono", monospace';
  ctx.fillText('MENO', x0, legendY + 11);
  LEVELS_BG.forEach((bg, i) => {
    const lx = x0 + 38 + i * (CELL + 4);
    ctx.fillStyle = bg;
    ctx.fillRect(lx, legendY, CELL, CELL);
    if (i === 0) {
      ctx.strokeStyle = borderEmpty;
      ctx.lineWidth = 1;
      ctx.strokeRect(lx + 0.5, legendY + 0.5, CELL - 1, CELL - 1);
    }
  });
  ctx.fillStyle = subColor;
  ctx.font = '600 9px "JetBrains Mono", monospace';
  ctx.fillText('PIÙ', x0 + 38 + LEVELS_BG.length * (CELL + 4) + 4, legendY + 11);

  return {
    width: blockWidth(hm.weeks.length),
    height: STATS_H + MONTH_LABEL_H + blockGridHeight() + LEGEND_H + 28,
  };
}

/**
 * Costruisce un canvas con la heatmap e ritorna { dataUrl, width, height }.
 */
export function renderHeatmapToPng({ heatmap, compare = null, dark = false }) {
  const blocks = compare ? [compare, heatmap] : [heatmap];
  const blockW = Math.max(...blocks.map((b) => blockWidth(b.weeks.length)));
  const blockH = STATS_H + MONTH_LABEL_H + blockGridHeight() + LEGEND_H + 28;

  const width = PAD * 2 + blockW;
  const height = PAD + HEADER_H + blocks.length * (blockH + 32) + FOOTER_H;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  // Background
  ctx.fillStyle = dark ? '#0A0A0A' : '#FAFAF7';
  ctx.fillRect(0, 0, width, height);

  // Header
  ctx.fillStyle = dark ? '#A3A3A3' : '#525252';
  ctx.font = '600 11px "JetBrains Mono", monospace';
  ctx.fillText('QUANTUM PLANNER · ESECUZIONE MIT', PAD, PAD + 12);

  ctx.fillStyle = dark ? '#FAFAFA' : '#171717';
  ctx.font = '700 42px "Inter", sans-serif';
  const title = compare
    ? `Heatmap ${compare.year} vs ${heatmap.year}`
    : `Heatmap ${heatmap.year}`;
  ctx.fillText(title, PAD, PAD + 56);

  // Sottotitolo
  ctx.fillStyle = dark ? '#A3A3A3' : '#525252';
  ctx.font = '400 13px "Inter", sans-serif';
  const subtitle = compare
    ? 'Confronto anno su anno · una cella per giorno · più verde = più MIT chiuse.'
    : 'Una cella per giorno · più verde = più MIT chiuse.';
  ctx.fillText(subtitle, PAD, PAD + 80);

  // Blocks
  let yCur = PAD + HEADER_H;
  blocks.forEach((b) => {
    drawBlock(ctx, PAD, yCur, b, { dark });
    yCur += blockH + 32;
  });

  // Footer
  ctx.fillStyle = dark ? '#525252' : '#A3A3A3';
  ctx.font = '400 10px "JetBrains Mono", monospace';
  const stamp = new Date().toISOString().slice(0, 10);
  ctx.fillText(`Generato il ${stamp} · quantumplanner.it`, PAD, height - PAD + 10);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width,
    height,
  };
}

/**
 * Trigger download dell'immagine PNG.
 */
export function downloadHeatmapPng({ heatmap, compare = null, dark = false }) {
  const { dataUrl } = renderHeatmapToPng({ heatmap, compare, dark });
  const a = document.createElement('a');
  const stamp = new Date().toISOString().slice(0, 10);
  const suffix = compare ? `${compare.year}-vs-${heatmap.year}` : `${heatmap.year}`;
  a.href = dataUrl;
  a.download = `quantumplanner-heatmap-${suffix}-${stamp}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
